// openEHR RM model — BMM package org.openehr.rm.data_types.basic
// Generated by tasks/split_model_by_bmm_packages.ts from tasks/bmm_package_map.json.
// Package boundaries follow the BMM package structure; edit the class bodies here,
// but re-run the splitter rather than hand-moving declarations between packages.

import type { DV_CODED_TEXT } from "./text.ts";
import * as openehr_base from "../../base/mod.ts";
import { applyRmInit, type RmInit } from "../../base/apply_rm_init.ts";

/**
 * Abstract parent of all \`DV_\` data value types.
 */
export abstract class DATA_VALUE extends openehr_base.OPENEHR_DEFINITIONS {
}

/**
 * Items which are truly boolean data, such as true/false or yes/no answers. For such data, it is important to devise the meanings (usually questions in subjective data)  carefully, so that the only allowed results are in fact true or false.
 *
 * Misuse: The DV_BOOLEAN class should not be used as a replacement for naively modelled enumerated types such as male/female etc. Such values should be coded, and in any case the enumeration often has more than two values.
 */
export class DV_BOOLEAN extends DATA_VALUE {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

  /**
   * Internal storage for value
   * @protected
   */
  protected _value?: openehr_base.Boolean;

  /**
   * Boolean value of this item. Actual values may be language or implementation dependent.
   */
  get value(): boolean | undefined {
    return this._value?.value;
  }

  /**
   * Gets the openehr_base.Boolean wrapper object for value.
   * Use this to access openehr_base.Boolean methods.
   */
  get $value(): openehr_base.Boolean | undefined {
    return this._value;
  }

  /**
   * Sets value from either a primitive value or openehr_base.Boolean wrapper.
   */
  set value(val: boolean | openehr_base.Boolean | undefined) {
    if (val === undefined || val === null) {
      this._value = undefined;
    } else if (typeof val === "boolean") {
      this._value = openehr_base.Boolean.from(val);
    } else {
      this._value = val;
    }
  }
}

/**
 * For representing state values which obey a defined state machine, such as a variable  representing the states of an instruction or care process.
 *
 * DV_STATE is expressed as a String but its values are driven by archetype-defined  state machines. This provides a powerful way of capturing stateful complex processes  in simple data.
 */
export class DV_STATE extends DATA_VALUE {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

  /**
   * The state name. State names are determined by a state/event table defined in archetypes, and coded using openEHR Terminology or local archetype terms, as specified by the archetype.
   */
  value?: DV_CODED_TEXT;
  /**
   * Internal storage for is_terminal
   * @protected
   */
  protected _is_terminal?: openehr_base.Boolean;

  /**
   * Indicates whether this state is a terminal state, such as  "aborted",  "completed" etc. from which no further transitions are possible.
   */
  get is_terminal(): boolean | undefined {
    return this._is_terminal?.value;
  }

  /**
   * Gets the openehr_base.Boolean wrapper object for is_terminal.
   * Use this to access openehr_base.Boolean methods.
   */
  get $is_terminal(): openehr_base.Boolean | undefined {
    return this._is_terminal;
  }

  /**
   * Sets is_terminal from either a primitive value or openehr_base.Boolean wrapper.
   */
  set is_terminal(val: boolean | openehr_base.Boolean | undefined) {
    if (val === undefined || val === null) {
      this._is_terminal = undefined;
    } else if (typeof val === "boolean") {
      this._is_terminal = openehr_base.Boolean.from(val);
    } else {
      this._is_terminal = val;
    }
  }
}

/**
 * Type for representing identifiers of real-world entities. Typical identifiers include drivers licence number, social security number, veterans affairs number, prescription id, order id, and so on.
 *
 * DV_IDENTIFIER is used to represent any identifier of a real thing, issued by some authority or agency.
 *
 * Misuse: DV_IDENTIFIER is not used to express identifiers generated by the infrastructure to refer to information items; the types OBJECT_ID and OBJECT_REF and subtypes are defined for this purpose.
 */
export class DV_IDENTIFIER extends DATA_VALUE {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

  /**
   * Internal storage for issuer
   * @protected
   */
  protected _issuer?: openehr_base.String;

  /**
   * Optional authority which issues the kind of id used in the id field of this object.
   */
  get issuer(): string | undefined {
    return this._issuer?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for issuer.
   * Use this to access openehr_base.String methods.
   */
  get $issuer(): openehr_base.String | undefined {
    return this._issuer;
  }

  /**
   * Sets issuer from either a primitive value or openehr_base.String wrapper.
   */
  set issuer(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._issuer = undefined;
    } else if (typeof val === "string") {
      this._issuer = openehr_base.String.from(val);
    } else {
      this._issuer = val;
    }
  }

  /**
   * Internal storage for assigner
   * @protected
   */
  protected _assigner?: openehr_base.String;

  /**
   * Optional organisation that assigned the id to the item being identified.
   */
  get assigner(): string | undefined {
    return this._assigner?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for assigner.
   * Use this to access openehr_base.String methods.
   */
  get $assigner(): openehr_base.String | undefined {
    return this._assigner;
  }

  /**
   * Sets assigner from either a primitive value or openehr_base.String wrapper.
   */
  set assigner(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._assigner = undefined;
    } else if (typeof val === "string") {
      this._assigner = openehr_base.String.from(val);
    } else {
      this._assigner = val;
    }
  }

  /**
   * Internal storage for id
   * @protected
   */
  protected _id?: openehr_base.String;

  /**
   * The identifier value. Often structured, according to the definition of the issuing authority's rules.
   */
  get id(): string | undefined {
    return this._id?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for id.
   * Use this to access openehr_base.String methods.
   */
  get $id(): openehr_base.String | undefined {
    return this._id;
  }

  /**
   * Sets id from either a primitive value or openehr_base.String wrapper.
   */
  set id(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._id = undefined;
    } else if (typeof val === "string") {
      this._id = openehr_base.String.from(val);
    } else {
      this._id = val;
    }
  }

  /**
   * Internal storage for type
   * @protected
   */
  protected _type?: openehr_base.String;

  /**
   * Optional identifier type, such as  prescription , or  Social Security Number . One day a controlled vocabulary might be possible for this.
   */
  get type(): string | undefined {
    return this._type?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for type.
   * Use this to access openehr_base.String methods.
   */
  get $type(): openehr_base.String | undefined {
    return this._type;
  }

  /**
   * Sets type from either a primitive value or openehr_base.String wrapper.
   */
  set type(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._type = undefined;
    } else if (typeof val === "string") {
      this._type = openehr_base.String.from(val);
    } else {
      this._type = val;
    }
  }
}
